import Head from 'next/head';
import React, { useEffect, useRef, useState } from 'react';

import { ArrowVariants } from '@/components/switchback/Carousel/Arrow';
import Carousel from '@/components/switchback/Carousel/Carousel';
import { IImage } from '@/services/types/search/rentals/id';
import formatImageURL from '@/utility/formatImageURL';
import { isSSR } from '@/utility/isSSR';

import CarouselItem from './CarouselItem';
import css from './GalleryCarousel.module.css';

type TImage = IImage & { alt?: string };

interface IGalleryCarousel {
  images: TImage[];
  activeSlide?: number;
  onChange?: (index: number) => void;
  /** this flag prevents repositioning of cursor pointer if iamge reference is changed */
  ignoreImageChange?: boolean;
}

const GalleryCarousel: React.FC<IGalleryCarousel> = ({
  images,
  activeSlide,
  onChange,
  ignoreImageChange,
}) => {
  const carouselRef: React.MutableRefObject<any> = useRef();
  const gallerylRef = useRef<HTMLDivElement>(null);
  const [nextImageLink, setNextImageLink] = useState('');
  const [classNames, setClassNames] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement> | KeyboardEvent) => {
    carouselRef.current && carouselRef.current.keyBoardNavigation(e);
  };

  const pauseVideo = (index: number) => {
    if (!gallerylRef.current) {
      return;
    }
    const parent = gallerylRef.current.querySelector('[data-index="' + index + '"]');
    const video = parent?.querySelector('iframe');
    const message = JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' });
    video?.contentWindow?.postMessage(message, '*');
  };

  const preLoadImages = (index: number) => {
    const nextImage = images[index + 1];
    if (!nextImage) return;
    const transformedSrc = formatImageURL(nextImage.url, 'default', 'fill');
    setNextImageLink(transformedSrc);
  };

  const beforeSlideChange = (oldIndex: number, newIndex: number) => {
    pauseVideo(oldIndex);
    return preLoadImages(newIndex);
  };

  const afterSlideChange = (index: number) => {
    onChange && onChange(index);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    !isSSR() && window.requestAnimationFrame(() => setClassNames('animate-slideIn'));
  }, [images]);

  useEffect(() => {
    if (ignoreImageChange) return;
    setClassNames('opacity-0');
    carouselRef.current && carouselRef.current.setPosition(0);
  }, [images, ignoreImageChange]);

  return (
    <>
      <Head key="imagepreload">
        <link href={nextImageLink} rel="preload" as="image" />
      </Head>
      <div ref={gallerylRef}>
        <Carousel
          ref={carouselRef}
          componentName="gallery-overlay"
          activeSlide={activeSlide}
          className={`${css.gallery} animate-fadeIn`}
          infinite={false}
          afterSlideChange={afterSlideChange}
          beforeSlideChange={beforeSlideChange}
          lazyLoad="ondemand"
          arrowVariant={[
            ArrowVariants.inside,
            ArrowVariants.inside,
            ArrowVariants.inside,
            ArrowVariants.outside,
          ]}>
          {images.map(image => (
            <CarouselItem key={image.id} image={image} className={classNames} />
          ))}
        </Carousel>
      </div>
    </>
  );
};

export default GalleryCarousel;
